import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ScreenshotService } from './screenshot.service';
import { CaptureRect, CaptureResult, ScreenshotState } from './screenshot.models';

/**
 * Fullscreen overlay that dims the page, tracks the user's drag-select and
 * (once a capture exists) hosts the floating toolbar. Only renders itself
 * when the service state !== 'idle', so the DOM is empty in the common case.
 */
@Component({
  selector: 'app-screenshot-overlay',
  templateUrl: './screenshot-overlay.component.html',
  styleUrls: ['./screenshot-overlay.component.scss'],
  standalone: false,
})
export class ScreenshotOverlayComponent implements OnInit, OnDestroy {
  state: ScreenshotState = 'idle';
  capture: CaptureResult | null = null;

  /** Live selection rectangle in viewport coordinates. */
  rect: CaptureRect | null = null;
  isDragging = false;

  private startX = 0;
  private startY = 0;
  private destroy$ = new Subject<void>();

  constructor(private screenshotService: ScreenshotService) {}

  ngOnInit(): void {
    this.screenshotService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((s) => {
        this.state = s;
        if (s === 'idle') {
          this.resetSelection();
        }
      });

    this.screenshotService.capture$
      .pipe(takeUntil(this.destroy$))
      .subscribe((c) => (this.capture = c));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------------
  // Selection drag handlers
  // ---------------------------------------------------------------------

  onSelectMouseDown(event: MouseEvent): void {
    if (this.state !== 'selecting') return;
    event.preventDefault();
    this.isDragging = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.rect = { x: this.startX, y: this.startY, width: 0, height: 0 };
  }

  @HostListener('window:mousemove', ['$event'])
  onWindowMouseMove(event: MouseEvent): void {
    if (!this.isDragging || this.state !== 'selecting') return;
    const x = Math.min(this.startX, event.clientX);
    const y = Math.min(this.startY, event.clientY);
    const width = Math.abs(event.clientX - this.startX);
    const height = Math.abs(event.clientY - this.startY);
    this.rect = { x, y, width, height };
  }

  @HostListener('window:mouseup', ['$event'])
  async onWindowMouseUp(_event: MouseEvent): Promise<void> {
    if (!this.isDragging || this.state !== 'selecting') return;
    this.isDragging = false;

    // Click without drag → treat as cancel.
    if (!this.rect || this.rect.width < 4 || this.rect.height < 4) {
      this.screenshotService.cancel();
      return;
    }

    await this.screenshotService.capture(this.rect);
  }

  // ---------------------------------------------------------------------
  // Cancel affordances
  // ---------------------------------------------------------------------

  @HostListener('window:keydown.escape')
  onEscape(): void {
    if (this.state !== 'idle') this.screenshotService.cancel();
  }

  onBackdropClickWhileCaptured(event: MouseEvent): void {
    // In "captured" state the toolbar has its own stopPropagation, so any
    // click that reaches the backdrop is outside the toolbar → cancel.
    if (this.state !== 'captured') return;
    event.stopPropagation();
    this.screenshotService.cancel();
  }

  private resetSelection(): void {
    this.rect = null;
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
  }
}
